import { useMemo, useState } from 'react';

import { WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { UseBaseTable } from 'hooks/useBaseTable';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useMutationExportCsvReport } from 'api/mutations/exportCsvReport.mutation';
import { useQueryStartDateChangesTableData } from 'api/queries/getTopStartDateChanges.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { TKeys } from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { statusOptions as initialStatusOptions } from 'components/widgets/TopStartDateChanges/startDateChanges.utils';

interface Props {
    widgetId?: string;
    globalFilterData: any;
    localFilters: any;
    compareVersionId: number | undefined;
    latestVersionId: number | undefined;
    projectId: number | null | undefined;
    contractId: number | null | undefined;
    table: UseBaseTable['table'];
    widgetViewMode: WidgetViewMode;
    endpoints: Record<string, string>;
}

export const useStartDateChangesProvider = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    localFilters,
    table,
    widgetViewMode,
    endpoints,
}: Props) => {
    const isInPinboard = Boolean(widgetId);

    const [statusOptions, setStatusOptions] = useState<ISelectOption<string>[]>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) && localFilters?.statusOptions
            ? localFilters.statusOptions
            : initialStatusOptions,
    );
    const [isCriticalPath, setIsCriticalPath] = useState<boolean>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) && localFilters?.isCriticalPath
            ? localFilters.isCriticalPath
            : false,
    );
    const [activeTab, setActiveTab] = useState<TKeys>(
        (WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ||
            WidgetViewModeUtils.isReportOrPinboardView(widgetViewMode)) &&
            localFilters?.activeTab
            ? (localFilters?.activeTab as TKeys)
            : 'next',
    );

    const pinboardState = { ...localFilters, activeTab };
    const pageState = { ...globalFilterData, statusOptions, isCriticalPath, activeTab, sorting: table.sorting };

    const source = isInPinboard ? pinboardState : pageState;

    const filters = useMemo(
        () =>
            getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.isExclude)
                    .map((i) => i.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.isExclude)
                    .map((i) => i.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.WBS_SEARCH]: source?.wbs,
                [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
            }),
        [source.activityCodes, source.wbs, source.tags, source.milestoneFragnet],
    );

    const getExportCsvReportPayload = ({ filters }) => {
        return ({ reportType, fileName }) => ({
            compareVersionId: compareVersionId,
            contractId: contractId,
            filter: { filters },
            latestVersionId: latestVersionId,
            period: source.activeTab,
            projectId: projectId,
            reportType,
            fileName,
        });
    };

    return {
        ui: {
            state: getState({ source, compareVersionId, contractId, latestVersionId, projectId }),
            isInPinBoard: isInPinboard,
            setters: {
                setIsCriticalPath,
                setStatusOptions,
                setActiveTab,
            },
            pills: useGetPills(source, widgetId, latestVersionId),
        },
        api: {
            getStartDateChanges: {
                instance: useQueryStartDateChangesTableData(
                    getStartDateChangesPayload({ compareVersionId, contractId, latestVersionId, filters, projectId }),
                    endpoints.getTopStartDateChanges,
                ),
                payload: getStartDateChangesPayload({
                    compareVersionId,
                    contractId,
                    latestVersionId,
                    filters,
                    projectId,
                }),
                endpoint: endpoints.getTopStartDateChanges,
            },
            exportCsvReport: {
                instance: useMutationExportCsvReport(),
                createPayload: getExportCsvReportPayload({ filters }),
            },
        },
    };
};

const getState = ({ projectId, contractId, latestVersionId, compareVersionId, source }) => {
    return {
        // project
        projectId,
        contractId,
        latestVersionId,
        compareVersionId,

        // global Filter
        activityCodes: source?.activityCodes,
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags,
        wbs: source?.wbs,

        // local filters
        isCriticalPath: source?.isCriticalPath,
        statusOptions: source?.statusOptions,
        activeTab: source?.activeTab,
        sorting: source?.sorting,
    };
};

const getStartDateChangesPayload = ({ projectId, contractId, latestVersionId, compareVersionId, filters }) => ({
    projectId,
    contractId,
    latestVersionId,
    compareVersionId,
    filter: {
        filters,
    },
});

const useGetPills = (source, widgetId, latestVersionId) => {
    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
        wbs: source?.wbs,
        isCriticalPath: source?.isCriticalPath,
        statusFilter: source?.statusOptions?.map((item) => item.label),
    };

    const { pillsList: pills } = useGlobalFiltersHook({
        latestVersionId,
        source: pillsData,
    });
    return pills;
};
